import { keyBy } from '@/utils/data/collections';
import {
  diffVerificationVerdicts,
  processRehydration,
  type ProcessRehydrationResult,
} from '@/utils/validation/validationUtils';
import type { StoreApi } from 'zustand';
import type { WorkflowStore } from '@/stores/workflowStore';
import type { ValidationStore, ValidationResult } from '@/stores/validationStore';
import type { FormDangerousGoodsItem, SubstanceVerificationResponse } from '@/api/types';
import type { ValidationFormData } from '@/types/validationFormTypes';

/**
 * Store-binding ports for commit primitives — dependency-inverted so the React hook
 * (`useValidationCommit`, scoped store) and the loader (`hydrateTransaction`,
 * `globalValidationStore`) share one implementation.
 */
export interface CommitPorts {
  validation: StoreApi<ValidationStore>;
  workflow: StoreApi<WorkflowStore>;
  writeGoods?: (goods: FormDangerousGoodsItem[], changedCargoIds: readonly string[]) => void;
}

export interface CommitOptions {
  /** Workflow mode writes updated form data back to workflowStore. Dialog mode omits. */
  syncWorkflow?: boolean;
}

/**
 * Push a commit's goods outwards. The workflow store is the dialog-optional half; the live
 * form is written either way, since the dialog edits through RHF too.
 */
function syncForm(
  ports: CommitPorts,
  formData: ValidationFormData,
  updatedGoods: FormDangerousGoodsItem[],
  changedCargoIds: readonly string[],
  syncWorkflow: boolean
): void {
  if (syncWorkflow) {
    ports.workflow.getState().setFormData({ ...formData, dangerousGoods: updatedGoods });
  }
  if (changedCargoIds.length > 0) ports.writeGoods?.(updatedGoods, changedCargoIds);
}

export interface VerificationCommit extends ProcessRehydrationResult {
  /** Cargo whose verdict changed. Only these lose their errors; the rest would come back the same. */
  movedCargoIds: string[];
}

/** Full verify response: set comparisons, merge results, clear errors for cargo whose verdict moved, optionally sync form. */
export function commitVerification(
  ports: CommitPorts,
  response: SubstanceVerificationResponse,
  formData: ValidationFormData,
  options: CommitOptions = { syncWorkflow: true }
): VerificationCommit {
  const { validationResults, validationByCargoId } = ports.validation.getState();
  const processed = processRehydration(response, formData, validationResults);
  ports.validation.getState().setComparisons(response.comparisons);

  const movedCargoIds = diffVerificationVerdicts(
    validationByCargoId,
    keyBy(processed.mergedResults, (r) => r.cargoId),
    (response.results ?? []).map((r) => r.cargoId)
  );

  ports.validation.getState().batchCommit(processed.mergedResults, {
    clearErrorsFor: movedCargoIds,
  });

  syncForm(
    ports,
    formData,
    processed.updatedGoods,
    processed.changedCargoIds,
    options.syncWorkflow ?? true
  );
  return { ...processed, movedCargoIds };
}

/** Already-processed results from substance selection: commit + clear errors for explicit cargo, optionally sync form. */
export function commitMerged(
  ports: CommitPorts,
  args: {
    mergedResults: ValidationResult[];
    updatedGoods: FormDangerousGoodsItem[];
    formData: ValidationFormData;
    affectedCargoIds: readonly string[];
    options?: CommitOptions;
  }
): void {
  ports.validation.getState().batchCommit(args.mergedResults, {
    clearErrorsFor: args.affectedCargoIds,
  });
  syncForm(
    ports,
    args.formData,
    args.updatedGoods,
    args.affectedCargoIds,
    args.options?.syncWorkflow ?? true
  );
}

/** Server-validated results during transaction load: no comparisons-set, no error-clear, no toast. */
export function commitRehydrated(
  ports: CommitPorts,
  response: SubstanceVerificationResponse,
  formData: ValidationFormData
): ProcessRehydrationResult | null {
  if (!response.results?.length) return null;

  const { validationResults } = ports.validation.getState();
  const processed = processRehydration(response, formData, validationResults);

  ports.validation.getState().batchCommit(processed.mergedResults);
  syncForm(ports, formData, processed.updatedGoods, processed.changedCargoIds, true);
  return processed;
}
